import {useState, useEffect} from 'react'
import {useNavigate} from 'react-router-dom'
import {CircularProgress, Box} from '@mui/material'
import axios from 'axios'
import {API_URL} from '../../../../_metronic/global/data'
import {useSnackbar} from 'notistack'

export function OnboardingPaymentSuccess() {
  const navigate = useNavigate()
  const {enqueueSnackbar} = useSnackbar()
  const [loading, setLoading] = useState(true)
  const [message, setMessage] = useState('Payment successful! Processing...')

  useEffect(() => {
    // Helper function to clear localStorage
    const clearRegistrationData = () => {
      localStorage.removeItem('registration_module_url')
      localStorage.removeItem('registration_auth_token')
      localStorage.removeItem('registration_business_data')
    }

    const handlePaymentSuccess = async () => {
      try {
        // Get data from localStorage FIRST
        const moduleUrl = localStorage.getItem('registration_module_url')
        const authData =
          localStorage.getItem('idToken') || localStorage.getItem('registration_auth_token')
        const businessData = localStorage.getItem('registration_business_data')

        let token = ''
        let businessId = ''

        // Extract auth token
        if (authData) {
          try {
            const parsedAuth = JSON.parse(authData)
            token = parsedAuth.api_token || parsedAuth.token || parsedAuth.accessToken || ''
          } catch (error) {
            token = authData
          }
        }

        // Extract business ID
        if (businessData) {
          try {
            const parsedBusinessData = JSON.parse(businessData)
            businessId = parsedBusinessData.business_id || ''
          } catch (error) {}
        }

        // Validate required data BEFORE showing loader
        if (!moduleUrl) {
          clearRegistrationData()
          enqueueSnackbar('Module URL not found. Redirecting to dashboard...', {
            variant: 'warning',
          })
          navigate('/dashboard')
          return
        }

        if (!token) {
          clearRegistrationData()
          enqueueSnackbar('Authentication token not found. Please login again.', {
            variant: 'error',
          })
          navigate('/auth/login')
          return
        }

        if (!businessId) {
          clearRegistrationData()
          enqueueSnackbar('Business ID not found. Redirecting to dashboard...', {
            variant: 'warning',
          })
          navigate('/dashboard')
          return
        }

        // All data is present - show success message and loader for 4 seconds

        setMessage('Payment successful! Processing...')
        await new Promise((resolve) => setTimeout(resolve, 4000))

        setMessage('Authenticating with module...')

        // Call module-auth API
        const response = await axios.post(
          `${API_URL}users/module-auth`,
          {
            moduleUrl: moduleUrl,
            businessId: businessId,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          }
        )

        if (response.status === 200 || response.status === 201) {
          const moduleToken = response.data.token

          if (!moduleToken) {
            clearRegistrationData()
            enqueueSnackbar('Module authentication failed. Redirecting to dashboard...', {
              variant: 'error',
            })
            navigate('/dashboard')
            return
          }

          // Construct redirect URL with token
          const redirectUrl = new URL(moduleUrl)
          redirectUrl.searchParams.append('token', moduleToken)

          // Clear registration data from localStorage before redirect
          clearRegistrationData()

          // Redirect to module URL with token
          window.location.href = redirectUrl.toString()
        } else {
          throw new Error('Module authentication failed')
        }
      } catch (error: any) {
        const errorMessage =
          error.response?.data?.message || error.message || 'Failed to authenticate with module'
        enqueueSnackbar(errorMessage, {variant: 'error'})

        // Clear registration data even on error
        clearRegistrationData()

        // Redirect to dashboard on error
        setTimeout(() => {
          navigate('/dashboard')
        }, 2000)
      } finally {
        setLoading(false)
      }
    }

    handlePaymentSuccess()
  }, [navigate, enqueueSnackbar])

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        padding: '20px',
      }}
    >
      <div className='w-lg-500px bg-body rounded shadow-sm p-10 p-lg-15 mx-auto text-center'>
        <h1 className='text-dark mb-5'>Payment Successful!</h1>

        {loading ? (
          <>
            <CircularProgress size={60} className='mb-5' />
            <p className='text-gray-600 fs-5'>{message}</p>
            <p className='text-gray-500 fs-6 mt-3'>Please wait, do not close this window...</p>
          </>
        ) : (
          <>
            <div className='alert alert-success mb-5'>
              <div className='alert-text'>Redirecting to your module...</div>
            </div>
          </>
        )}
      </div>
    </Box>
  )
}
